import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { Shell } from '../../components/layout/Shell';
import { ItemForm } from '../../components/inventory/ItemForm';
import { ImageAnalyzer } from '../../components/inventory/ImageAnalyzer';
import { useToast } from '../../components/ui/Toast';
import { useCreateInventoryItem, useUploadItemImage } from '../../hooks/useInventory';
import { Route as rootRoute } from '../__root';
import type { CreateInventoryItemInput } from '@siliconvault/shared';
import { useTranslation } from '../../i18n';
import styles from './new.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory/new',
  component: NewItemPage,
});

function NewItemPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const createMutation = useCreateInventoryItem();
  const uploadImageMutation = useUploadItemImage();
  const { t } = useTranslation();

  const [initialData, setInitialData] = useState<Partial<CreateInventoryItemInput> | undefined>(undefined);
  const [formKey, setFormKey] = useState(0);
  const pendingImageRef = useRef<File | null>(null);
  const [scannerSlot, setScannerSlot] = useState<HTMLDivElement | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

  const scannerSlotRef = useCallback((el: HTMLDivElement | null) => {
    setScannerSlot(el);
  }, []);

  function handleAnalysisComplete(
    data: { name: string; category: string; platform: string; subcategory?: string | null; condition: string; videoSystem?: string | null; description: string; cpu?: string | null; memory?: string | null; launchDate?: string | null; launchPrice?: string | null; launchPriceCurrency?: string | null; launchPriceInflationAdjusted?: string | null },
    imageFile: File,
  ) {
    setInitialData({
      name: data.name,
      category: data.category,
      platform: data.platform,
      condition: data.condition as CreateInventoryItemInput['condition'],
      videoSystem: data.videoSystem as CreateInventoryItemInput['videoSystem'],
      subcategory: data.subcategory ?? null,
      description: data.description,
      metadata: {
        ...(data.cpu ? { cpu: data.cpu } : {}),
        ...(data.memory ? { memory: data.memory } : {}),
        ...(data.launchDate ? { launchDate: data.launchDate } : {}),
        ...(data.launchPrice ? { launchPrice: data.launchPrice } : {}),
        ...(data.launchPriceCurrency ? { launchPriceCurrency: data.launchPriceCurrency } : {}),
        ...(data.launchPriceInflationAdjusted ? { launchPriceInflationAdjusted: data.launchPriceInflationAdjusted } : {}),
      },
    });
    pendingImageRef.current = imageFile;
    setFormKey((k) => k + 1);
    toast(t('aiScan.prefilled'), 'success');
  }

  async function handleSubmit(data: CreateInventoryItemInput) {
    try {
      const item = await createMutation.mutateAsync(data);

      // Auto-upload the scanned image if available
      if (pendingImageRef.current) {
        try {
          await uploadImageMutation.mutateAsync({
            itemId: item.id,
            file: pendingImageRef.current,
          });
        } catch {
          // Image upload failure is non-blocking
        }
        pendingImageRef.current = null;
      }

      toast('Item created successfully', 'success');
      navigate({ to: '/inventory/$itemId', params: { itemId: item.id }, search: { from: undefined } });
    } catch {
      toast('Failed to create item', 'error');
    }
  }

  return (
    <Shell>
      <div className={styles.page}>
        <Link to="/inventory" className={styles.backLink}>
          &larr; {t('itemDetail.backToInventory')}
        </Link>
        <h1 className={styles.title}>{t('itemDetail.addNewItem')}</h1>
        <ItemForm
          key={formKey}
          initialData={initialData}
          onSubmit={handleSubmit}
          onCancel={() => navigate({ to: '/inventory' })}
          isLoading={createMutation.isPending}
          submitLabel={t('itemDetail.createItem')}
          scannerSlotRef={scannerSlotRef}
        />
        {scannerSlot && createPortal(
          <ImageAnalyzer
            onAnalysisComplete={handleAnalysisComplete}
            onImageSelected={(file) => { pendingImageRef.current = file; }}
            imageFile={imageFile}
            imagePreviewUrl={imagePreviewUrl}
            onImageChange={(file, url) => {
              setImageFile(file);
              setImagePreviewUrl(url);
              pendingImageRef.current = file;
            }}
          />,
          scannerSlot,
        )}
      </div>
    </Shell>
  );
}
